import React, {useEffect, useState} from 'react';
import Icon from '@/shared/components/Icon';
import {toast} from 'react-toastify';
import type {Recommendation} from '@/shared/types/api';
import {logError} from '@/shared/utils/logger';
import BioMatchEnrollmentService, {isCheckoutIntent} from '../services/bioMatchEnrollmentService';
import EnrollConfirmModal from './EnrollConfirmModal';
import BioMatchCheckoutModal from './BioMatchCheckoutModal';

/**
 * One BioMatch tile inside the horizontal carousel. Visual contract
 * matches the legacy `ProductCard` (263px wide, 160px image, category
 * chip, name, description, price area, full-width CTA at the bottom)
 * so the patient dashboard reads as a single coherent surface.
 *
 * Behavior varies by `recommendation_type`:
 *   - program        : "Enroll" toast (real enrollment infra ships later)
 *   - care_pathway   : opens `pathway_url` in a new tab
 *   - lifestyle      : opens `url` in a new tab; renders price + members_price
 *   - supplement     : same as lifestyle (legacy default)
 *
 * Critical referrals don't use this card — they render as a separate
 * full-width banner above the carousel.
 */

interface Props {
    rec: Recommendation;
}

// Maps `risk_tier` to the chip color used in the category pill,
// matching the existing dashboard palette.
const TIER_CHIP_BG: Record<string, string> = {
    critical: '#fda4af', // rose-300
    high: '#fdba74', // orange-300
    moderate: '#fcd34d', // amber-300
    low: '#86efac', // green-300
};

const TIER_LABEL: Record<string, string> = {
    critical: 'CRITICAL',
    high: 'HIGH',
    moderate: 'MODERATE',
    low: 'LOW',
};

const TYPE_LABEL: Record<string, string> = {
    program: 'Care Program',
    care_pathway: 'Care Pathway',
    lifestyle: 'Lifestyle',
    supplement: 'Supplement',
    referral: 'Urgent',
};

const stripHtml = (html: string | null | undefined): string => {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
};

const BioMatchCarouselCard: React.FC<Props> = ({rec}) => {
    const type = rec.recommendation_type ?? 'supplement';
    const tier = rec.risk_tier ?? null;
    const tierChipBg = tier ? TIER_CHIP_BG[tier] : '#F0F1EB';
    const categoryLabel = tier ? TIER_LABEL[tier] : TYPE_LABEL[type] ?? 'BioMatch';

    const description = rec.cta_body_text || stripHtml(rec.content) || 'Based on your screening results';
    const showPrice = type === 'lifestyle' || type === 'supplement';
    const buttonLabel =
        rec.cta_button_label ||
        (type === 'program'
            ? 'Enroll'
            : type === 'care_pathway'
              ? 'See if I qualify'
              : type === 'lifestyle' || type === 'supplement'
                ? rec.url
                    ? 'Learn More'
                    : 'Contact Us'
                : 'Learn More');

    // Local state — modals open + post-enroll "you're in" lock so the
    // patient sees instant feedback without refetching.
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [enrolled, setEnrolled] = useState(false);
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [pendingPatientCents, setPendingPatientCents] = useState(0);

    // Fire a dedup'd "view" impression once per render. Server-side
    // dedup means refresh loops don't inflate counts.
    useEffect(() => {
        BioMatchEnrollmentService.recordImpression(rec.id, 'view');
    }, [rec.id]);

    const handleConfirm = async () => {
        try {
            const result = await BioMatchEnrollmentService.enroll(rec.id);
            if (isCheckoutIntent(result)) {
                // Patient owes money — hand off to the BioMatch
                // checkout modal which mounts the Fortis Elements
                // widget. Enrollment row is only created after Fortis
                // confirms the charge.
                setPendingPatientCents(result.patient_amount_cents);
                setConfirmOpen(false);
                setCheckoutOpen(true);
                return;
            }
            // Free dispatch. Distinguish add-to-draw from home-kit so the
            // patient understands what's happening next.
            if (result.fulfillment_method === 'add_to_existing_draw') {
                toast.success(
                    'Enrolled. We\'ll add these tests to your recent blood sample — no new draw needed.',
                    {autoClose: 5000}
                );
            } else {
                toast.success('Enrollment recorded. Our team will reach out with next steps.');
            }
            setEnrolled(true);
            setConfirmOpen(false);
        } catch (error) {
            const status = (error as {status?: number} | undefined)?.status;
            if (status === 409) {
                toast.info('You are already enrolled in this program.');
                setEnrolled(true);
                setConfirmOpen(false);
                return;
            }
            logError('BioMatch enrollment failed', error);
            toast.error('Could not enroll. Please try again.');
        }
    };

    const handleClick = () => {
        BioMatchEnrollmentService.recordImpression(rec.id, 'click');
        if (type === 'program') {
            if (enrolled) {
                toast.info('You are already enrolled in this program.');
                return;
            }
            setConfirmOpen(true);
            return;
        }
        if (type === 'care_pathway') {
            if (rec.pathway_url) {
                window.open(rec.pathway_url, '_blank', 'noopener,noreferrer');
            } else {
                toast.info('This pathway will be available soon.');
            }
            return;
        }
        if (rec.url) {
            window.open(rec.url, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <div className="bg-white rounded-lg border border-mld-panel-border flex-shrink-0 w-[263px] sm:w-[243px] touch-manipulation overflow-hidden flex flex-col">
            <div className="h-[160px] overflow-hidden">
                {rec.image ? (
                    <img src={rec.image} alt={rec.name} className="w-full h-full object-cover" />
                ) : (
                    // Placeholder banner when no image is attached. Tints by
                    // tier so a glance conveys severity even without art.
                    <div
                        className="h-full w-full flex items-center justify-center"
                        style={{backgroundColor: tier ? tierChipBg : '#e5e7eb'}}>
                        <Icon name="circle-exclamation" className="h-10 w-10 text-white opacity-90" />
                    </div>
                )}
            </div>

            <div className="border-t border-mld-panel-border w-full" />

            <div className="p-4 pt-3 flex-1 flex flex-col">
                <div className="mb-3">
                    <span
                        className="inline-block text-xs px-2 py-1 rounded text-mld-card-text"
                        style={{backgroundColor: tierChipBg}}>
                        {categoryLabel}
                    </span>
                </div>

                <h4
                    className="text-base font-normal text-mld-card-text mb-2 text-left leading-tight"
                    style={{
                        fontFamily: 'Poppins',
                        fontWeight: 400,
                        fontSize: '21px',
                        lineHeight: '25.2px',
                        letterSpacing: '0%',
                    }}>
                    {rec.name}
                </h4>

                <div className="flex items-start text-xs text-mld-card-text mb-2">
                    <svg
                        className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2">
                        <polyline points="22,12 18,12 15,21 9,3 6,12 2,12"></polyline>
                    </svg>
                    <span className="line-clamp-3">{description}</span>
                </div>

                <div className="flex-1">
                    {showPrice && (
                        <>
                            <p
                                className="text-2xl font-regular text-mld-card-text mb-1 text-left"
                                style={{
                                    fontFamily: 'Poppins',
                                    fontWeight: 400,
                                    fontSize: '23px',
                                    lineHeight: 'auto',
                                    letterSpacing: '0%',
                                }}>
                                {rec.price ? `Starts at $${rec.price.toFixed(2)}` : 'Contact for pricing'}
                            </p>
                            {rec.members_price && (
                                <p className="text-xs text-mld-card-text mb-3 text-left">
                                    Only ${rec.members_price.toFixed(2)} with Membership
                                </p>
                            )}
                        </>
                    )}
                </div>

                <button
                    type="button"
                    onClick={handleClick}
                    disabled={type === 'program' && enrolled}
                    className="bg-mld-green-button text-mld-button-text font-medium py-2 px-4 rounded-full text-sm w-full hover:opacity-90 transition-opacity duration-200 mt-auto disabled:opacity-60 disabled:hover:opacity-60">
                    {type === 'program' && enrolled ? 'Enrolled' : buttonLabel}
                </button>
            </div>

            {type === 'program' && (
                <>
                    <EnrollConfirmModal
                        open={confirmOpen}
                        programName={rec.name}
                        bodyText={rec.cta_body_text}
                        confirmLabel={rec.cta_button_label}
                        onConfirm={handleConfirm}
                        onClose={() => setConfirmOpen(false)}
                    />
                    <BioMatchCheckoutModal
                        open={checkoutOpen}
                        recommendationId={rec.id}
                        programName={rec.name}
                        bodyText={rec.cta_body_text}
                        patientAmountCents={pendingPatientCents}
                        onSuccess={() => {
                            setEnrolled(true);
                            setCheckoutOpen(false);
                        }}
                        onClose={() => setCheckoutOpen(false)}
                    />
                </>
            )}
        </div>
    );
};

export default BioMatchCarouselCard;
